import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";
import { shippingSummary } from "../utils/shipping";

const formatPrice = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value)
    : value;

function Delivery() {
  const shipping = siteConfig.shipping || {};
  const contact = siteConfig.contact || {};

  return (
    <main className="content-page policy-page shipping-page">
      <header className="page-hero">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Shipping &amp; delivery</p>
          <h1>From our pantry to yours.</h1>
          <p className="page-hero__lede">
            We pack every {siteConfig.brandName} order carefully and keep you
            informed from checkout through delivery.
          </p>
        </div>
      </header>

      <section className="page-section page-shell">
        <div className="policy-summary-grid" aria-label="Shipping at a glance">
          <article className="policy-summary-card">
            <p className="eyebrow">Standard shipping</p>
            <h2>{formatPrice(shipping.maximumCharge)}</h2>
            <p>One flat rate for supported destinations.</p>
          </article>
          <article className="policy-summary-card">
            <p className="eyebrow">Simple pricing</p>
            <h2>{formatPrice(shipping.flatRate)}</h2>
            <p>No per-item shipping surcharge.</p>
          </article>
          <article className="policy-summary-card">
            <p className="eyebrow">Order preparation</p>
            <h2>{shipping.processingTime}</h2>
            <p>
              Orders placed on weekends begin processing the next business day.
            </p>
          </article>
          <article className="policy-summary-card">
            <p className="eyebrow">Estimated transit</p>
            <h2>{shipping.deliveryWindow}</h2>
            <p>
              Transit estimates begin once the carrier collects your package.
            </p>
          </article>
        </div>
      </section>

      <section className="page-section page-section--tinted">
        <div className="page-shell policy-layout">
          <nav className="policy-nav" aria-label="On this page">
            <p className="eyebrow">On this page</p>
            <a href="#shipping-process">How shipping works</a>
            <a href="#shipping-area">Where we ship</a>
            <a href="#shipping-international">International orders</a>
            <a href="#shipping-perishable">Food in transit</a>
            <a href="#shipping-changes">Changes &amp; delays</a>
            <a href="#shipping-damage">Delivery issues</a>
          </nav>

          <div className="prose policy-copy">
            <section id="shipping-process">
              <h2>How shipping works</h2>
              <p>{shippingSummary(shipping)}</p>
              <ol>
                <li>
                  <strong>Order confirmation.</strong> We email you after
                  checkout with a summary of your order.
                </li>
                <li>
                  <strong>Careful packing.</strong> Glass, tins, and delicate
                  pantry goods are protected for their journey.
                </li>
                <li>
                  <strong>Tracking.</strong> Once the carrier accepts the
                  package, you receive a tracking link by email.
                </li>
                <li>
                  <strong>Delivery.</strong> Carrier scans are the best source
                  for the latest arrival estimate.
                </li>
              </ol>
            </section>

            <section id="shipping-international">
              <h2>International orders, duties, and import rules</h2>
              <p>
                Cross-border availability depends on the product, destination,
                carrier, and local import rules. Checkout availability does not
                guarantee that a destination authority will admit every food.
                We may pause or cancel an affected item if a known restriction
                prevents lawful delivery.
              </p>
              <p>
                Any duties, import taxes, brokerage, or similar destination
                charges will be identified at checkout when collected by us.
                If they are payable to a carrier or authority on arrival, that
                responsibility will be disclosed before purchase where known.
              </p>
            </section>

            <section id="shipping-perishable">
              <h2>Food, heat, and time in transit</h2>
              <p>
                {shipping.perishableNotice || "Heat-sensitive items may require protective packaging or a delayed dispatch."}
                {" "}Follow the storage directions on the delivered label and
                inspect seals and packaging promptly.
              </p>
              <p>
                Do not consume a leaking, swollen, broken, recalled, or otherwise
                unsafe-looking product. Keep the batch information and packaging,
                then follow the <Link to="/food-safety">food safety policy</Link>
                {" "}and contact us.
              </p>
            </section>

            <section id="shipping-area">
              <h2>Where we ship</h2>
              <p>
                Standard delivery is available to the countries listed at
                checkout. Destinations subject to applicable sanctions are not
                offered. A complete, secure delivery address is required.
              </p>
              <p>
                Some products may have additional destination or carrier
                restrictions. Any restriction will appear before you complete
                your purchase.
              </p>
            </section>

            <section id="shipping-changes">
              <h2>Address changes and delays</h2>
              <p>
                Contact us as soon as possible if an address needs to be
                corrected. We can update it only before the order is packed or
                transferred to the carrier. We cannot guarantee rerouting after
                shipment.
              </p>
              <p>
                Severe weather, holidays, carrier congestion, and other events
                outside our control can extend delivery estimates. We will share
                any delay we know about, but shipping fees are not refundable
                for a carrier delay alone unless the selected service, our offer,
                or applicable law provides otherwise.
              </p>
            </section>

            <section id="shipping-damage">
              <h2>Missing, damaged, or incorrect deliveries</h2>
              <p>
                If an item arrives damaged or your order is incomplete, keep the
                packaging and, where possible, contact us within 48 hours of
                delivery. This reporting target helps us preserve carrier and
                batch evidence; it does not shorten any non-excludable consumer
                right. Include your order number and clear photos so we can make
                it right quickly.
              </p>
              <p>
                {contact.email ? (
                  <a href={`mailto:${contact.email}`}>Email {contact.email}</a>
                ) : (
                  <Link to="/contact">Contact customer care</Link>
                )}{" "}
                or review our <Link to="/returns">returns policy</Link> for the
                next steps.
              </p>
              <p>
                Safety concerns are also covered by our{" "}
                <Link to="/recalls">product recall policy</Link>. Nothing on this
                page excludes a consumer guarantee that applies by law.
              </p>
              <p><Link to="/policies">View all store policies</Link>.</p>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Delivery;
